import { useEffect, useState } from "react";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import { toast } from "sonner";
import { Save, Loader2, RotateCcw, Info, ShieldCheck } from "lucide-react";

const FIELD_META = {
    direct_bonus_gross: {
        label: "Direct Referral Bonus (Gross)",
        unit: "₹",
        step: "1",
        hint: "Total amount paid when a member directly refers someone who activates. Net = Gross × (1 − Deduction %).",
    },
    pairing_bonus_per_pair: {
        label: "Pairing Bonus per Pair (Gross)",
        unit: "₹",
        step: "1",
        hint: "Total amount credited per matched left-right pair in the nightly pairing run. Net = Gross × (1 − Deduction %).",
    },
    daily_pair_limit: {
        label: "Daily Pair Limit",
        unit: "pairs/day",
        step: "1",
        hint: "Maximum number of new pairs a single member can be credited in one nightly run.",
    },
    business_cut_percent: {
        label: "Total Business Deduction",
        unit: "%",
        step: "0.01",
        hint: "Total deduction % from every bonus. Should equal admin_charge + TDS + travel fund. Informational — actual computation uses the three sub-percentages below.",
    },
    admin_charge_percent: {
        label: "Admin Charges",
        unit: "%",
        step: "0.01",
        hint: "Portion of gross bonus charged as platform admin fees. Goes to the business wallet. Typically 15% of gross.",
    },
    tds_percent: {
        label: "TDS (Tax Deducted at Source)",
        unit: "%",
        step: "0.01",
        hint: "Portion of gross bonus withheld as TDS. Held by business for tax compliance. Typically 5% of gross.",
    },
    travel_fund_percent: {
        label: "Travel Fund",
        unit: "%",
        step: "0.01",
        hint: "Portion of gross bonus credited to the member's travel fund. Claimable when accumulated balance ≥ ₹500. Typically 5% of gross.",
    },
    bonus_settlement_days: {
        label: "Bonus Hold Period",
        unit: "days",
        step: "1",
        hint: "Number of days after credit before a bonus becomes withdrawable by the member.",
    },
};

function DerivedRow({ gross, adminPct, tdsPct, fundPct, label }) {
    if (!gross || adminPct == null) return null;
    const g     = parseFloat(gross);
    const admin = (g * parseFloat(adminPct) / 100).toFixed(2);
    const tds   = (g * parseFloat(tdsPct)   / 100).toFixed(2);
    const fund  = (g * parseFloat(fundPct)  / 100).toFixed(2);
    const net   = (g - parseFloat(admin) - parseFloat(tds) - parseFloat(fund)).toFixed(2);
    return (
        <div className="mt-2 space-y-1 text-xs leading-relaxed">
            <p className="text-teal-600 font-semibold">→ Net {label}: ₹{net}</p>
            <div className="ml-3 space-y-0.5 text-slate-400">
                <p><span className="text-orange-500 font-medium">− Admin charges ({adminPct}%):</span> ₹{admin}</p>
                <p><span className="text-red-500 font-medium">− TDS ({tdsPct}%):</span> ₹{tds}</p>
                <p><span className="text-blue-500 font-medium">→ Travel fund ({fundPct}%):</span> ₹{fund}</p>
            </div>
        </div>
    );
}

export default function AdminSettings() {
    const [items, setItems]     = useState([]);
    const [draft, setDraft]     = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving]   = useState(false);
    const [resetting, setResetting] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/config");
            setItems(data);
            const vals = {};
            data.forEach(item => { vals[item.key] = item.value; });
            setDraft(vals);
        } catch {
            toast.error("Failed to load platform settings");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleChange = (key, val) => {
        setDraft(prev => ({ ...prev, [key]: val }));
    };

    const isDirty = (key) => {
        const item = items.find(i => i.key === key);
        return item && String(draft[key]) !== item.value;
    };

    const anyDirty = items.some(i => isDirty(i.key));

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {};
            items.forEach(item => {
                if (isDirty(item.key)) {
                    payload[item.key] = item.type === "int"
                        ? parseInt(draft[item.key], 10)
                        : parseFloat(draft[item.key]);
                }
            });
            if (!Object.keys(payload).length) {
                toast.info("No changes to save");
                return;
            }
            await api.patch("/admin/config", payload);
            toast.success("Settings saved successfully");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to save settings");
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async (key) => {
        setResetting(key);
        try {
            await api.delete(`/admin/config/${key}`);
            toast.success(`"${FIELD_META[key]?.label || key}" reset to default`);
            load();
        } catch {
            toast.error("Failed to reset");
        } finally {
            setResetting(null);
        }
    };

    const adminPct = draft["admin_charge_percent"] ?? 15;
    const tdsPct   = draft["tds_percent"]          ?? 5;
    const fundPct  = draft["travel_fund_percent"]  ?? 5;
    const dGross   = parseFloat(draft["direct_bonus_gross"]      ?? 0);
    const pGross   = parseFloat(draft["pairing_bonus_per_pair"]  ?? 0);

    return (
        <AppShell admin>
            {/* ── Header ── */}
            <div className="mb-8">
                <div className="overline">Admin</div>
                <h1 className="font-display font-black text-3xl tracking-tighter text-slate-900 mt-2">
                    Platform Settings
                </h1>
                <p className="text-sm text-slate-500 mt-1">
                    Configure bonus amounts, deduction %, and settlement rules. Changes take effect on the next pairing run or the next member activation.
                </p>
            </div>

            {loading ? (
                <div className="flex items-center gap-2 text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
            ) : (
                <div className="max-w-2xl space-y-5">

                    {/* Security notice */}
                    <div className="flex gap-3 bg-blue-50 border border-blue-100 rounded-2xl p-4">
                        <ShieldCheck className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                        <p className="text-sm text-blue-700">
                            Only admin accounts can view or change these settings.
                            Each save is logged with your email and timestamp.
                            Infrastructure secrets (DB password, API keys) are managed via environment variables and are never stored here.
                        </p>
                    </div>

                    {/* Setting cards */}
                    {items.map(item => {
                        const meta   = FIELD_META[item.key] || {};
                        const dirty  = isDirty(item.key);
                        const custom = item.is_customized;

                        return (
                            <div key={item.key}
                                className={`bg-white rounded-2xl shadow-sm border transition-all
                                    ${dirty ? "border-amber-300 ring-1 ring-amber-200" : "border-slate-100"}`}>
                                <div className="p-5">
                                    {/* Label row */}
                                    <div className="flex items-start justify-between gap-3 mb-3">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-semibold text-slate-800 text-sm">
                                                    {meta.label || item.key}
                                                </span>
                                                {custom && !dirty && (
                                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200">
                                                        CUSTOMISED
                                                    </span>
                                                )}
                                                {dirty && (
                                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                                                        UNSAVED
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-start gap-1 mt-0.5">
                                                <Info className="w-3 h-3 text-slate-300 shrink-0 mt-0.5" />
                                                <p className="text-[11px] text-slate-400 leading-relaxed">
                                                    {meta.hint || item.description}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Reset to default button */}
                                        {custom && (
                                            <button
                                                onClick={() => handleReset(item.key)}
                                                disabled={resetting === item.key}
                                                title={`Reset to default (${item.default})`}
                                                className="shrink-0 text-slate-400 hover:text-rose-500 transition-colors p-1.5 rounded-lg hover:bg-rose-50"
                                            >
                                                {resetting === item.key
                                                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                    : <RotateCcw className="w-3.5 h-3.5" />
                                                }
                                            </button>
                                        )}
                                    </div>

                                    {/* Input */}
                                    <div className="flex items-center gap-2">
                                        <div className="relative flex-1">
                                            {meta.unit && (
                                                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400 pointer-events-none select-none">
                                                    {meta.unit === "₹" ? "₹" : ""}
                                                </span>
                                            )}
                                            <input
                                                type="number"
                                                step={meta.step || "1"}
                                                min="0"
                                                value={draft[item.key] ?? ""}
                                                onChange={e => handleChange(item.key, e.target.value)}
                                                className={`w-full rounded-xl border py-2.5 text-sm font-semibold text-slate-800 outline-none
                                                    focus:ring-2 focus:ring-[#2172E5]/20 focus:border-[#2172E5] transition-colors
                                                    ${meta.unit === "₹" ? "pl-7 pr-4" : "pl-4 pr-4"}
                                                    ${dirty ? "border-amber-300 bg-amber-50/30" : "border-slate-200 bg-slate-50"}`}
                                            />
                                        </div>
                                        <span className="text-xs font-semibold text-slate-400 shrink-0 w-16">
                                            {meta.unit !== "₹" ? meta.unit : "gross"}
                                        </span>
                                    </div>

                                    {/* Derived net preview */}
                                    {item.key === "direct_bonus_gross" && (
                                        <DerivedRow gross={dGross} adminPct={adminPct} tdsPct={tdsPct} fundPct={fundPct} label="per direct referral" />
                                    )}
                                    {item.key === "pairing_bonus_per_pair" && (
                                        <DerivedRow gross={pGross} adminPct={adminPct} tdsPct={tdsPct} fundPct={fundPct} label="per pair" />
                                    )}

                                    {/* Last changed */}
                                    {custom && item.updated_by && !dirty && (
                                        <p className="text-[10px] text-slate-300 mt-2">
                                            Last changed by {item.updated_by}
                                            {item.updated_at
                                                ? ` on ${new Date(item.updated_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}`
                                                : ""}
                                        </p>
                                    )}
                                    {!custom && (
                                        <p className="text-[10px] text-slate-300 mt-2">
                                            Using default: {item.default} {meta.unit !== "₹" ? meta.unit : ""}
                                        </p>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    {/* Save bar */}
                    <div className={`sticky bottom-4 flex items-center justify-between gap-3 bg-white border rounded-2xl px-5 py-4 shadow-lg transition-all
                        ${anyDirty ? "border-amber-300 shadow-amber-100" : "border-slate-100"}`}>
                        <p className="text-sm text-slate-500">
                            {anyDirty
                                ? <><span className="font-semibold text-amber-600">Unsaved changes</span> — review before saving</>
                                : "All settings are saved"}
                        </p>
                        <button
                            onClick={handleSave}
                            disabled={saving || !anyDirty}
                            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#2172E5] hover:bg-[#1a5ccc]
                                text-white text-sm font-semibold transition-all shadow-sm
                                disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saving
                                ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                                : <><Save className="w-4 h-4" /> Save Changes</>
                            }
                        </button>
                    </div>
                </div>
            )}
        </AppShell>
    );
}
